import React, { FunctionComponent, ReactNode } from 'react';
import { classNames } from '../../utils/class-names';

// Tailwind replacements for the react-bootstrap Form family. The styling
// baseline comes from @tailwindcss/forms; these add the spacing, validation
// and label conventions the app used.

// Carries the resolved controlId from Form.Group down to Form.Label and
// Form.Control so they associate automatically, mirroring react-bootstrap.
const FormGroupContext = React.createContext<string | undefined>(undefined);

interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  noValidate?: boolean;
  children?: ReactNode;
}

const Form: FunctionComponent<FormProps> & {
  Group: FunctionComponent<{ className?: string; controlId?: string; children?: ReactNode }>;
  Label: FunctionComponent<{ className?: string; htmlFor?: string; children?: ReactNode }>;
  Control: typeof FormControl;
  Text: FunctionComponent<{ className?: string; muted?: boolean; children?: ReactNode }>;
  Check: typeof FormCheck;
} = ({ children, ...rest }) => <form {...rest}>{children}</form>;

Form.Group = ({ className, controlId, children }) => {
  const generatedId = React.useId();
  const resolvedId = controlId ?? generatedId;

  return (
    <FormGroupContext.Provider value={resolvedId}>
      <div className={classNames('mb-4', className || '')}>{children}</div>
    </FormGroupContext.Provider>
  );
};

Form.Label = ({ className, htmlFor, children }) => {
  const contextId = React.useContext(FormGroupContext);
  const resolvedHtmlFor = htmlFor ?? contextId;

  return (
    <label
      htmlFor={resolvedHtmlFor}
      className={classNames('block font-bold mb-1', className || '')}
    >
      {children}
    </label>
  );
};

interface FormControlProps extends React.InputHTMLAttributes<HTMLInputElement> {
  as?: 'textarea' | 'input' | 'select';
  rows?: number;
  isInvalid?: boolean;
  [key: string]: any;
}

const FormControl = React.forwardRef<HTMLInputElement, FormControlProps>(
  ({ as, isInvalid, size, className, children, id, ...rest }, ref) => {
    const contextId = React.useContext(FormGroupContext);
    const resolvedId = id ?? contextId;
    const baseClasses = classNames(
      'block w-full rounded-md shadow-xs sm:text-sm',
      isInvalid
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-secondary focus:ring-secondary',
      className || ''
    );

    if (as === 'textarea') {
      return (
        <textarea ref={ref as any} {...(rest as any)} id={resolvedId} className={baseClasses} />
      );
    }

    if (as === 'select') {
      return (
        <select ref={ref as any} {...(rest as any)} id={resolvedId} className={baseClasses}>
          {children}
        </select>
      );
    }

    return (
      <input
        ref={ref}
        type={rest.type || 'text'}
        {...rest}
        id={resolvedId}
        className={baseClasses}
      />
    );
  }
);
FormControl.displayName = 'FormControl';
Form.Control = FormControl;

Form.Text = ({
  className,
  children,
}: {
  className?: string;
  muted?: boolean;
  children?: ReactNode;
}) => <div className={classNames('mt-1 text-sm text-gray-500', className || '')}>{children}</div>;

interface FormCheckProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type?: 'checkbox' | 'radio' | any;
  label?: ReactNode;
  id?: string;
  [key: string]: any;
}

const FormCheck: FunctionComponent<FormCheckProps> = ({
  type = 'checkbox',
  label,
  id,
  className,
  ...rest
}) => (
  <div className={classNames('flex items-center gap-2 py-1', className || '')}>
    <input
      id={id}
      type={type}
      {...rest}
      className={classNames(
        'border-gray-300 text-secondary focus:ring-secondary',
        type === 'radio' ? 'rounded-full' : 'rounded-sm'
      )}
    />
    {label && (
      <label htmlFor={id} className="m-0 text-sm text-gray-700 font-normal">
        {label}
      </label>
    )}
  </div>
);
Form.Check = FormCheck;

export default Form;
